<template>
    <div class="row" style="min-height: 280px;" v-if="vans.length">
        <div class="col">
            <h3 class="h4 text-center my-4 mb-5 pb-4"><strong>Based on your trade and size of load you carry we recommend you consider the following van types</strong></h3>
            <transition name="fade">
                <div class="vans" v-if="vans.length">
                    <slick ref="slickVans" class="slickVans"
                           :options="slickVansOptions"
                           v-match-heights="{el: ['.vans__name']}"
                           @breakpoint="slickHandleBreakpoint">
                        <span class="vans__item" v-for="(van, index) in vans" :key="index"
                              @click="getVan(index)"
                              :class="{active: selectedVan == index}">
                            <svg viewBox="-2 0 108 104" class="vans__image">
                                <use v-bind="{ 'xlink:href': iconName(van.name) + (selectedVan == index ? '_active' : '') }"></use>
                            </svg>
                            <p class="paragraph vans__name"><strong>{{van.name}}</strong></p>
                        </span>
                    </slick>
                </div>
            </transition>

            <transition name="fade">
                <div class="van" v-if="notEmptyObject(van)">
                    <h3>{{ van.name }}</h3>
                    <p class="paragraph">{{ van.explanation }}</p>
                    <div class="van__highlights row no-gutters"
                         v-match-heights="{el: ['.van__highlights h4', '.van__highlightsText']}">
                        <div class="col-lg" v-if="van.payload_max">
                            <h4>Payload up to:</h4>
                            <p class="van__highlightsText">{{ van.payload_max | toNumber }}</p>
                        </div>
                        <div class="col-lg" v-if="van.loadspace_volume_max">
                            <h4>Loadspace volume<span v-if="showDimensionDisclaimer">*</span> up to:</h4>
                            <p class="van__highlightsText" v-html="$options.filters.superscript(van.loadspace_volume_max)"></p>
                        </div>
                        <div class="col-lg" v-if="van.loadspace_length_max">
                            <h4>Loadspace length<span v-if="showDimensionDisclaimer">*</span> up to:</h4>
                            <p class="van__highlightsText">{{ van.loadspace_length_max | toNumber }}</p>
                        </div>
                        <div class="col-lg" v-if="van.loadspace_width_max">
                            <h4>Loadspace width<span v-if="showDimensionDisclaimer">*</span> up to:</h4>
                            <p class="van__highlightsText">{{ van.loadspace_width_max | toNumber }}</p>
                        </div>
                        <div class="col-lg" v-if="van.loadspace_height_max">
                            <h4>Loadspace height<span v-if="showDimensionDisclaimer">*</span> <small>(to top of load bed)</small>:</h4>
                            <p class="van__highlightsText">{{ van.loadspace_height_max | toNumber }}</p>
                        </div>
                        <div class="col-lg-4" v-if="van.other_features">
                            <h4>Other features:</h4>
                            <ul class="van__highlightsText">
                                <li v-for="(item, index) in (van.other_features.split('\n'))" :key="index">
                                    {{ item }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <p class="paragraph paragraph--small" v-if="showDimensionDisclaimer || showCrewDisclaimer">
                        * <span v-if="showDimensionDisclaimer">The maximum dimensions listed are intended to provide guidance to users. However some options may be available which extend the capability of certain models. If you would like to know exact details please consult the manufacturer website or contact us. </span>
                        <span v-if="showCrewDisclaimer">For all vans a Crew option is available, allowing you to carry more passengers but sacrificing payload and load space. If you would like to discuss this option please get in touch.</span>
                    </p>

                    <div v-if="van.vehicles.length">
                        <h3>Our most popular models</h3>
                        <div class="recommend">
                            <slick ref="slickModels" class="slickModels"
                                   :options="slickModelsOptions"
                                   v-match-heights="{el: ['.recommend__name', '.recommend__details']}"
                                   @breakpoint="slickHandleBreakpoint">
                                <div class="recommend__item" v-for="(model, index) in van.vehicles" :key="index">
                                    <div class="recommend__image">
                                        <img :src="'assets/img/featured/' + model.image_url"  alt="" class="img-fluid">
                                    </div>
                                    <p class="recommend__name paragraph mb-1"><strong>{{ model.name }}</strong></p>
                                    <ul class="recommend__details">
                                        <li v-html="'Payload: ' + $options.filters.toNumber(model.payload)"></li>
                                        <li v-html="'Loadspace: ' + $options.filters.superscript(model.loadspace)"></li>
                                        <li>Wheelbase: {{ model.wheelbase }}</li>
                                    </ul>
                                    <p class="paragraph">
                                        <strong v-if="model.price > 0">
                                            Available from {{ model.price | toCurrency }} per month*<span v-if="showDimensionDisclaimer">*</span>
                                        </strong>
                                        <span v-else>&nbsp;</span>
                                    </p>
                                    <a :href="'https://www.vwfsfleet.co.uk/van-leasing/van-payload-advice/contact/?LCV_Selected_Vehicle=' + encodeURI(model.name)" :ref="'cta' + index" class="button button--solid recommend__cta text-center"
                                       @click.stop="trackCTA('cta' + index)" target="_blank">Contact our CV Specialists</a>
                                </div>
                            </slick>
                        </div>
                    </div>
                    <p class="paragraph paragraph--small">
                        Disclaimer: Payload Vanalyser is designed to offer guidance on which type(s) of van might be most appropriate for you, based on your answers to the questions provided. It should not be relied upon solely to inform your buying decision. We advise speaking to us for any specific needs, or seek independent advise and carefully considering your personal circumstances before making a final decision.
                        <br>
                        *<span v-if="showDimensionDisclaimer || showCrewDisclaimer">*</span> Business users only. 48 months, 20,000 miles per annum. Excluding VAT at the prevailing rate. Initial rental applies. Terms & Conditions apply. Over 18s only. No ownership. This quotation is valid until the end of the working month. Finance supplied by Volkswagen Financial Services | Fleet.
                    </p>
                </div>
            </transition>

            <modal v-show="isModalVisible" @close="closeModal">
                <div slot="body">
                    <div class="container-fluid">
                        <div class="row">
                            <div class="col">
                                <h2>Nearly done</h2>
                                <p class="paragraph">
                                    We’re ready to finalise your results, but we want to give you some relevant guides, tools and reports to better inform your journey to choosing the right van. Provide us with a bit more information and we’ll recommend the right content for you. Alternatively, you can <a href="javascript:void(0);" @click="closeModal">go back</a> to review and amend your answers.
                                </p>

                                <p class="alert alert-danger" v-if="errors.length">Please complete the highlighted fields</p>

                                <form action="#" method="post" class="form" novalidate @submit="formSubmit">
                                    <label for="firstname" class="label label--text">
                                        First name*
                                        <input type="text" name="firstname" v-model="firstname"  id="firstname" class="input input--text" required>
                                    </label>
                                    <label for="lastname" class="label label--text">
                                        Last name*
                                        <input type="text" name="lastname" v-model="lastname" id="lastname" class="input input--text" required>
                                    </label>
                                    <label for="company" class="label label--text">
                                        Company*
                                        <input type="text" name="company" v-model="company" id="company" class="input input--text" required>
                                    </label>
                                    <label for="email" class="label label--text">
                                        Email*
                                        <input type="text" name="email" v-model="email" id="email" class="input input--text" required>
                                    </label>
                                    <label for="fleetsize" class="label label--text">
                                        Fleet size*
                                        <div class="pretty-select">
                                            <select name="fleetsize" v-model="fleetsize" id="fleetsize">
                                                <option value="Small Business (1-4)">1-4</option>
                                                <option value="SME (5-25)">5-25</option>
                                                <option value="Corporate (26-499)">26-499</option>
                                                <option value="Strategic (500+)">500+</option>
                                                <option value="CV">Commercial Vehicles</option>
                                                <option value="Public Sector">Public Sector (1 - 500+)</option>
                                                <option value="Driver">Driver</option>
                                            </select>
                                        </div>
                                    </label>
                                    <label for="fleetcomp" class="label label--text">
                                        Fleet composition*
                                        <div class="pretty-select">
                                            <select name="fleetcomp" v-model="fleetcomp" id="fleetcomp">
                                                <option value="Cars">Cars</option>
                                                <option value="Vans">Vans</option>
                                                <option value="Mixed">Mixed</option>
                                            </select>
                                        </div>
                                    </label>

                                    <div>
                                        <div class="pretty-radio">
                                            <span class="pretty-radio__title" id="optin">Email marketing* | </span>
                                            <label class="label--radio" for="optin-in">
                                                <input class="input" type="radio" name="optin" v-model="optin"  id="optin-in" value="true">
                                                <span class="pretty-radio__title">Opt-in</span>
                                            </label>
                                            <label class="label--radio" for="optin-out">
                                                <input class="input" type="radio" name="optin"  v-model="optin" id="optin-out" value="false">
                                                <span class="pretty-radio__title">Opt-out</span>
                                            </label>
                                        </div>
                                        <br>
                                        <p class="paragraph--small text-muted">Volkswagen Financial Services | Fleet (VWFS | Fleet) would like to send you information about its products and services. If you agree, please tick to say how you would like to be contacted. You can change your preferences at any time by updating them online or writing to or calling us. If you do opt-out, you may still see some generic, non-personalised advertising when you are using digital channels.</p>
                                        <br>
                                    </div>

                                    <input type="submit" value="View results" class="button button--solid" :disabled="submitting">

                                    <div>
                                        <br>
                                        <p class="paragraph--small text-muted">Fields marked with an asterisk are required.</p>
                                        <p class="paragraph--small text-muted"><strong>About us and Privacy</strong></p>
                                        <p class="paragraph--small text-muted">VWFS | Fleet is collecting this personal data about you (“your information”) in order to respond to your enquiry.</p>
                                        <p class="paragraph--small text-muted">The privacy and security of your personal information is very important to us, so we want to assure you that your information will be properly managed and protected when it's in our hands.</p>
                                        <p class="paragraph--small text-muted">Further information on how your information is used, how we maintain the security of your information, your right to access information we hold on you and details of relevant third party and Volkswagen Group companies for data sharing purposes is in our <a href="https://www.vwfsfleet.co.uk/data-privacy-policy" target="_blank">Privacy Policy</a> or can be requested from <a href="mailto:DPO@vwfs.co.uk">DPO@vwfs.co.uk</a></p>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </modal>
        </div>
    </div>
</template>

<script>
    import Slick from './CustomSlick';
    import modal from './ModalForm';
    import axios from 'axios';
    import qs from 'qs';

    export default {
        name: 'VanList',

        components: {
            Slick,
            modal
        },

        data() {
            return {
                firstname: null,
                lastname: null,
                email: null,
                company: null,
                fleetsize: null,
                fleetcomp: null,
                optin: null,
                endpoint: process.env.VUE_APP_PARDOT_PROXY,
                pardotEndpoint: 'https://www2.vwfsfleet.co.uk/l/151741/2020-02-10/n5fpmn',
                submitting: false,

                required: [
                    'firstname',
                    'lastname',
                    'email',
                    'company',
                    'fleetsize',
                    'fleetcomp',
                    'optin'
                ],
                errors: [],

                isModalVisible: false,

                slickVansOptions: {
                    slidesToShow: 5,
                    infinite: false,
                    dots: true,
                    arrows: true,
                    appendArrows: '.slickVans + .paging',
                    appendDots: '.slickVans + .paging',
                    draggable: false,
                    swipeToSlide: true,

                    responsive: [
                        {
                            breakpoint: 991,
                            settings: {
                                slidesToShow: 3
                            }
                        },

                        {
                            breakpoint: 767,
                            settings: {
                                slidesToShow: 1
                            }
                        }
                    ]
                },

                slickModelsOptions: {
                    slidesToShow: 3,
                    infinite: false,
                    dots: true,
                    arrows: true,
                    appendArrows: '.slickModels + .paging',
                    appendDots: '.slickModels + .paging',
                    draggable: false,
                    swipeToSlide: true,

                    responsive: [
                        {
                            breakpoint: 991,
                            settings: {
                                slidesToShow: 2
                            }
                        },

                        {
                            breakpoint: 767,
                            settings: {
                                slidesToShow: 1
                            }
                        }
                    ]
                }
            };
        },

        methods: {
            showModal() {
                this.isModalVisible = true;
            },

            closeModal() {
                this.isModalVisible = false;
            },

            getVan(index) {
                this.$store.commit('selectedVan', index);

                var vanName = this.$store.getters.getVans[this.$store.getters.getSelectedVan].name;

                this.$gtag.logStep(
                    'Van Type',
                    'Payload Vanaliser - ' + this.getSelectedTradeName() + ' - ' + this.getSelectedLoadName() + ' - ' + vanName
                );

                if (this.$store.state.showGate) {
                    if (this.$cookies.isKey('PayloadVanalyserGate')) {
                      this.$store.commit('van', this.$store.getters.getSelectedVan);
                    } else {
                      if (!this.$store.state.gatedLogged) {
                        this.$gtag.logStep(
                            'Payload Vanaliser - Gate',
                            'open'
                        );
                        this.$store.commit('setGatedLogged', true);
                      }
                      this.showModal();
                    }
                } else {
                    this.$store.commit('van', this.$store.getters.getSelectedVan);
                }
            },

            iconName(name) {
                return '#' +name.replace(/[^a-z]+/gi, '_').toLowerCase()
            },

            trackCTA(ref) {
                this.$gtag.logCTA('Payload Vanaliser - ' + this.$refs[ref][0].innerText + ' - ' + this.$refs[ref][0].href);
                window.location.href = this.$refs[ref][0].href;
            },

            formSubmit(e) {
                e.preventDefault();
                this.errors = [];
                this.submitting = true;

                let errors = Array.from(this.$el.querySelectorAll('.error'));
                if (errors.length) {
                    errors.forEach(function(el){
                        el.classList.remove('error');
                    });
                }

                let valid = true,
                    vm = this;

                this.required.forEach(function(field){
                    if (vm[field] === null || vm[field] === '') {
                        vm.errors.push(field);
                        valid = false;
                        vm.submitting = false;
                        vm.$el.querySelector('#' + field).parentElement.classList.add('error');
                    }
                });

                if (valid === true) {
                    let pardot = axios.create();

                    pardot.post(this.endpoint,
                        qs.stringify({
                            firstname: this.firstname,
                            lastname: this.lastname,
                            email: this.email,
                            company:this.company,
                            fleetsize: this.fleetsize,
                            fleetcomp: this.fleetcomp,
                            optin: this.optin,
                            pardot: this.pardotEndpoint,
                            required: this.required
                        })
                    )
                        .then(function (response) {
                            vm.submitting = false;
                            let data = response.data;
                            if (data.success) {
                                vm.$gtag.logStep(
                                    'Payload Vanaliser - Gate',
                                    'success'
                                );
                                vm.$cookies.set('PayloadVanalyserGate', '1');
                                vm.$store.commit('van', vm.$store.getters.getSelectedVan);
                                vm.closeModal();
                            } else {
                                vm.$gtag.logStep(
                                    'Payload Vanaliser - Gate',
                                    'fail'
                                );
                                data.errors.forEach(function(field){
                                    vm.errors.push(field);
                                    vm.$el.querySelector('#' + field).parentElement.classList.add('error')
                                });
                            }
                        });
                }
            }
        },

        computed: {
            vans() {
                return this.$store.getters.getVans;
            },

            van() {
                if (this.$store.getters.getVan == null) {
                    return {};
                } else {
                    return this.vans[this.$store.getters.getVan];
                }
            },

            selectedVan() {
                return this.$store.getters.getVan
            },

            showDimensionDisclaimer() {
                let show = ['Small panel van', 'Medium panel van', 'Large panel van'];
                return show.indexOf(this.van.name) !== -1;
            },

            showCrewDisclaimer() {
                let show = ['Medium panel van', 'Large panel van'];
                return show.indexOf(this.van.name) !== -1;
            }
        },

        watch: {
            vans: function () {
                if (this.vans.length > 0) {
                    this.$nextTick(() => {
                        if (typeof this.$refs.slickVans !== 'undefined') {
                            this.refreshSlick('slickVans');
                        }
                    });
                }
            },
            van: function () {
                if (this.notEmptyObject(this.van)) {
                    this.$nextTick(() => {
                        if (typeof this.$refs.slickModels !== 'undefined') {
                            this.refreshSlick('slickModels');
                        }
                    });
                }
            }
        },

        filters : {
            superscript: function (value) {
                if (!value) return '';
                value = value.toString()
                return value.replace(/m3/, 'm<sup>3</sup>');
            }
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="stylus">
    .vans
        ::v-deep .slick-slider
            .slick-list,
            .slick-slide
                margin-right 0 !important

        ::v-deep .vans__item
            display block !important
            padding 1.5rem
            width 100%
            margin 0 auto
            background $color-grey
            text-align center
            cursor pointer

            &.active
                background-color $color-light-cyan

                .vans__image
                    transform: translateZ(0); // Force safari repaint

            .vans__name
                font-size 1.6rem
                text-transform uppercase
                margin-bottom 0
                margin-top 1.5rem

            .vans__image
                display block
                margin 0 auto
                width 100px
                height auto

    .van
        background $color-light-cyan
        padding 2.5rem

    .van__highlights
        margin-bottom 3rem
        margin-top 2.5rem

        h4
            font-size 1.6rem
            padding 1rem 1.5rem
            background-color: #004666
            color white
            margin 0

        .van__highlightsText
            font-size 1.6rem
            padding 1rem 1.5rem
            background-color white
            color #4a4a4a
            margin 0

        ul.van__highlightsText
            padding-left 3.5rem


    .recommend
        margin-bottom 2.5rem

        ::v-deep .recommend__item
            display block !important
            background $color-white
            padding 1.5rem
            margin 0 auto

            &:nth-child(even)
                background-color #e2e4e7

            .recommend__image
                display block
                margin -1.5rem -1.5rem 1.5rem

            .recommend__cta
                width 100%
</style>
